/**
 * Shared DC size + annual production resolution from RetrieveSystemDesign-shaped payloads.
 * Used by Design & Product Details and financing pricing so $ uses the same numbers as the card.
 */

import { GetRoundNum } from "../components/Layout-Components/Editor-Layout/Three-JS-Viewer/Controllers/Common";

export function coercePositiveKw(value: unknown): number | null {
    if (value == null || value === '') return null;
    if (typeof value === 'number' && Number.isFinite(value) && value > 0) return value;
    const s = String(value).replace(/,/g, '').replace(/kwh?/gi, '').trim();
    if (!s) return null;
    const n = parseFloat(s);
    return Number.isFinite(n) && n > 0 ? n : null;
}

export function parseDesignDataRecord(sd: Record<string, unknown>): Record<string, unknown> | null {
    const raw = sd.Design_Data;
    if (raw != null && typeof raw === 'object') return raw as Record<string, unknown>;
    if (typeof raw === 'string') {
        try {
            const parsed = JSON.parse(raw) as Record<string, unknown>;
            return parsed && typeof parsed === 'object' ? parsed : null;
        } catch {
            return null;
        }
    }
    return null;
}

/** DC system size (kW) from root, Design_Data, or Σ DesignArray (qty × module W / 1000). */
export function systemSizeKwFromSnapshot(sd: Record<string, unknown>, designData: Record<string, unknown> | null): number | null {
    const tryVals: unknown[] = [
        sd.SystemSize,
        sd.System_Size_kWdc,
        (sd as { systemSize?: unknown }).systemSize,
        (sd as { System_Size_AC_kW?: unknown }).System_Size_AC_kW,
        designData?.System_Size_kWdc,
        (designData as { system_size_kwdc?: unknown } | null)?.system_size_kwdc,
        (designData as { SystemSize?: unknown } | null)?.SystemSize,
    ];
    for (const v of tryVals) {
        const n = coercePositiveKw(v);
        if (n != null) return n;
    }
    const arr = sd.DesignArray;
    if (Array.isArray(arr) && arr.length > 0) {
        let sumKw = 0;
        for (const item of arr) {
            if (!item || typeof item !== 'object') continue;
            const row = item as Record<string, unknown>;
            const qty = Number(row.Quantity ?? row.ModuleCount ?? row.Module_Quantity) || 0;
            const w = Number(row.ModuleWattage ?? row.Module_Wattage ?? row.ModulePower) || 0;
            if (qty > 0 && w > 0) sumKw += (qty * w) / 1000;
        }
        if (sumKw > 0) return sumKw;
    }
    return null;
}

/** Parse production scalar from API numbers or unit strings (e.g. "10.097Kwh", "10,097 kWh"). */
export function parseProductionRawScalar(value: unknown): number | null {
    if (value == null || value === '') return null;
    if (typeof value === 'number' && Number.isFinite(value)) return value;
    const s = String(value).replace(/,/g, '').replace(/\s*kwh?\s*$/i, '').trim();
    if (!s) return null;
    const n = parseFloat(s);
    return Number.isFinite(n) ? n : null;
}

function productionRawFromDesignArrayFirst(arr: unknown): number | null {
    if (!Array.isArray(arr) || arr.length === 0) return null;
    const first = arr[0] as Record<string, unknown> | undefined;
    if (!first || typeof first !== 'object') return null;
    const v =
        first.Production ??
        first.Annual_Production_Kwh ??
        (first as { Estimated_Annual_Production_Kwh?: unknown }).Estimated_Annual_Production_Kwh ??
        (first as { ProductionKwh?: unknown }).ProductionKwh;
    return parseProductionRawScalar(v);
}

/** Sum annual production fields across DesignArray rows when present. */
function productionRawFromDesignArraySum(arr: unknown): number | null {
    if (!Array.isArray(arr) || arr.length === 0) return null;
    let sum = 0;
    let any = false;
    for (const item of arr) {
        if (!item || typeof item !== 'object') continue;
        const row = item as Record<string, unknown>;
        const v =
            row.Production ??
            row.Annual_Production_Kwh ??
            (row as { Estimated_Annual_Production_Kwh?: unknown }).Estimated_Annual_Production_Kwh ??
            (row as { ProductionKwh?: unknown }).ProductionKwh;
        const n = parseProductionRawScalar(v);
        if (n != null) {
            sum += n;
            any = true;
        }
    }
    return any ? sum : null;
}

/**
 * Raw production scalar as stored on design (same convention as ProductionsCard display / formatProductionValue).
 */
function productionFromApiValue(value: unknown): number | null {
    const direct = parseProductionRawScalar(value);
    if (direct != null) return direct;
    if (!value || typeof value !== 'object') return null;
    const nested = value as Record<string, unknown>;
    return (
        parseProductionRawScalar(nested.ActualAnnual) ??
        parseProductionRawScalar(nested.actualAnnual) ??
        parseProductionRawScalar(nested.Annual) ??
        parseProductionRawScalar(nested.annual)
    );
}

export function solarProductionRawFromSnapshot(sd: Record<string, unknown>, designData: Record<string, unknown> | null): number | null {
    const fromRoot =
        productionFromApiValue(sd.Production) ??
        parseProductionRawScalar(sd.Annual_Production_Kwh) ??
        parseProductionRawScalar(sd.Estimated_Annual_Production_Kwh) ??
        parseProductionRawScalar((sd as { estimated_Annual_Production_Kwh?: unknown }).estimated_Annual_Production_Kwh) ??
        parseProductionRawScalar(designData?.Annual_Production_Kwh) ??
        parseProductionRawScalar(
            (designData as { Estimated_Annual_Production_Kwh?: unknown } | null)?.Estimated_Annual_Production_Kwh,
        );
    if (fromRoot != null) return fromRoot;
    const sumArr = productionRawFromDesignArraySum(sd.DesignArray);
    if (sumArr != null) return sumArr;
    return productionRawFromDesignArrayFirst(sd.DesignArray);
}

/**
 * Annual kWh for pricing API: values ≥ 1000 treated as kWh already; smaller values × 1000 (MWh-style storage).
 */
export function annualProductionKwhForPricingFromRaw(raw: number | null | undefined): number | null {
    if (raw == null || !Number.isFinite(Number(raw))) return null;
    const prodRaw = Number(raw);
    if (prodRaw <= 0) return null;
    return prodRaw >= 1000 ? Math.round(prodRaw) : Math.round(prodRaw * 1000);
}

/** Annual production (kWh/yr) from system design snapshot for pricing / financing header. */
export function annualProductionKwhFromSnapshot(sd: Record<string, unknown> | null | undefined): number | null {
    if (!sd) return null;
    const designData = parseDesignDataRecord(sd);
    const raw = solarProductionRawFromSnapshot(sd, designData);
    return annualProductionKwhForPricingFromRaw(raw);
}

/** Baseline yield (kWh/kWdc/yr) when API/3D production is not yet available on dashboard-first load. */
const ESTIMATED_YIELD_KWH_PER_KW = 1000;

/**
 * Viewer `actualWattage` is annual kWh (legacy name). Use for finalize save and post-finalize sync.
 */
export function annualProductionKwhFromViewerActual(actualWattage: number | null | undefined): number | null {
    if (typeof actualWattage !== 'number' || !Number.isFinite(actualWattage) || actualWattage <= 0) {
        return null;
    }
    return Math.round(actualWattage);
}

/** Overlay finalized oneDRAW kWh - Actual onto RetrieveSystemDesign before dashboard display/save. */
export function applyFinalizedProductionToSystemDesign<T extends Record<string, unknown>>(
    design: T,
    actualKwh: number | null,
): T {
    if (actualKwh == null || actualKwh <= 0) return design;
    return {
        ...design,
        ProductionDouble: actualKwh/1000,
        Estimated_Annual_Production_Kwh: actualKwh,
        estimated_Annual_Production_Kwh: actualKwh,
        Annual_Production_Kwh: actualKwh,
    };
}

/** Overlay finalized oneDRAW system size + multiplier onto RetrieveSystemDesign before dashboard display/save. */
export function applyFinalizedSolarMetricsToSystemDesign<T extends Record<string, unknown>>(
    design: T,
    systemKw: number | null | undefined,
    multiplier: number | null | undefined,
): T {
    const hasSystemKw = typeof systemKw === 'number' && Number.isFinite(systemKw) && systemKw > 0;
    const hasMultiplier = typeof multiplier === 'number' && Number.isFinite(multiplier) && multiplier > 0;
    if (!hasSystemKw && !hasMultiplier) return design;

    const next: Record<string, unknown> = { ...design };

    if (hasSystemKw) {
        next.System_Size_kWdc = systemKw;
    }

    if (hasMultiplier) {
        const multRounded = GetRoundNum(multiplier, 2);
        next.Multiplier = multRounded;
        next.multiplier = multRounded;
    }

    const designData = parseDesignDataRecord(next);
    if (designData) {
        const patchedData = { ...designData };
        if (hasSystemKw) {
            patchedData.System_Size_kWdc = systemKw;
        }
        if (hasMultiplier) {
            const multRounded = GetRoundNum(multiplier, 2);
            patchedData.Multiplier = multRounded;
        }
        const raw = next.Design_Data;
        next.Design_Data = typeof raw === 'string' ? JSON.stringify(patchedData) : patchedData;
    }

    return next as T;
}

/** Overlay finalized oneDRAW viewer metrics onto RetrieveSystemDesign before dashboard display/save. */
export function applyFinalizedViewerMetricsToSystemDesign<T extends Record<string, unknown>>(
    design: T,
    viewer: {
        actualKwh?: number | null;
        systemKw?: number | null;
        multiplier?: number | null;
    },
): T {
    let result = design;
    result = applyFinalizedProductionToSystemDesign(result, viewer.actualKwh ?? null);
    result = applyFinalizedSolarMetricsToSystemDesign(result, viewer.systemKw ?? null, viewer.multiplier ?? null);
    return result;
}

export type ResolveSolarAnnualProductionOptions = {
    sd: Record<string, unknown>;
    designData?: Record<string, unknown> | null;
    viewerActualAnnualKwh?: number;
    viewerModeCount?: number;
    siteViewerCalculationsPending?: boolean;
    systemKw?: number | null;
    multiplier?: number | null;
    /** When true, never fall back to live 3D viewer (System & Design / finalized pricing). */
    snapshotOnly?: boolean;
};

/** Finalized oneDRAW design priced from System & Design (not an AI / tree scenario card). */
export function isFinalizedProductsSolarPricingDesign(
    design: { layoutKey?: string } | null | undefined,
): boolean {
    return design?.layoutKey === 'products-solar';
}

const SYSTEM_KW_SYNC_TOLERANCE_ABS = 0.02;
const SYSTEM_KW_SYNC_TOLERANCE_REL = 0.005;
const MULTIPLIER_SYNC_TOLERANCE_ABS = 0.001;
const MULTIPLIER_SYNC_TOLERANCE_REL = 0.005;

export type SolarMetricsSyncCheck = {
    finalizedSystemKw: number | null;
    finalizedMultiplier: number | null;
    liveSystemKw: number | null;
    liveMultiplier: number | null;
    calculationsPending?: boolean;
    liveModuleCount?: number;
};

function metricValuesDiffer(a: number, b: number, absTol: number, relTol: number): boolean {
    return Math.abs(a - b) > Math.max(absTol, Math.abs(a) * relTol);
}

/** Which finalized metrics drifted from live oneDRAW, so cards can point at the exact value. */
export type SolarMetricsSyncDetail = {
    outOfSync: boolean;
    systemKwOutOfSync: boolean;
    multiplierOutOfSync: boolean;
    /** Live oneDRAW values, only set when they are comparable to the finalized snapshot. */
    liveSystemKw: number | null;
    liveMultiplier: number | null;
};

const IN_SYNC: SolarMetricsSyncDetail = {
    outOfSync: false,
    systemKwOutOfSync: false,
    multiplierOutOfSync: false,
    liveSystemKw: null,
    liveMultiplier: null,
};

/** Per-metric comparison of saved system design against live oneDRAW Total System Efficiency. */
export function getSolarMetricsSyncDetail(check: SolarMetricsSyncCheck): SolarMetricsSyncDetail {
    const {
        finalizedSystemKw,
        finalizedMultiplier,
        liveSystemKw,
        liveMultiplier,
        calculationsPending,
        liveModuleCount,
    } = check;

    if (calculationsPending) return IN_SYNC;
    if (finalizedSystemKw == null || finalizedMultiplier == null) return IN_SYNC;
    if (liveSystemKw == null || liveMultiplier == null) return IN_SYNC;
    if ((liveModuleCount ?? 0) <= 0) return IN_SYNC;

    const systemKwOutOfSync = metricValuesDiffer(
        finalizedSystemKw,
        liveSystemKw,
        SYSTEM_KW_SYNC_TOLERANCE_ABS,
        SYSTEM_KW_SYNC_TOLERANCE_REL,
    );
    const multiplierOutOfSync = metricValuesDiffer(
        GetRoundNum(finalizedMultiplier, 2),
        GetRoundNum(liveMultiplier, 2),
        MULTIPLIER_SYNC_TOLERANCE_ABS,
        MULTIPLIER_SYNC_TOLERANCE_REL,
    );

    return {
        outOfSync: systemKwOutOfSync || multiplierOutOfSync,
        systemKwOutOfSync,
        multiplierOutOfSync,
        liveSystemKw,
        liveMultiplier,
    };
}

/** True when saved system-design system size / multiplier differ from live oneDRAW Total System Efficiency. */
export function isSolarFinalizedMetricsOutOfSync(check: SolarMetricsSyncCheck): boolean {
    return getSolarMetricsSyncDetail(check).outOfSync;
}

export const SOLAR_NON_FINALIZED_WARNING =
    'oneDRAW has some unfinalized changes. Please finalize.';

export const ONEDRAW_DESIGN_NOT_LOADED_MESSAGE = 'The oneDRAW design was not loaded.';

/**
 * Annual kWh for dashboard System Design cards: RetrieveSystemDesign snapshot first
 * (same source as system size), then kW × multiplier from that design, then live 3D.
 * Finalized inline Financing Options uses the same snapshot-first resolver.
 */
export function resolveSolarAnnualProductionKwh(options: ResolveSolarAnnualProductionOptions): number | null {
    const { sd, viewerActualAnnualKwh, siteViewerCalculationsPending, viewerModeCount } = options;
    const designData = options.designData ?? parseDesignDataRecord(sd);

    const fromSnapshot = annualProductionKwhFromSnapshot(sd);
    if (fromSnapshot != null && fromSnapshot > 0) {
        return fromSnapshot;
    }

    const systemKw = options.systemKw ?? systemSizeKwFromSnapshot(sd, designData);
    const multRaw = options.multiplier ?? sd.Multiplier_Unrounded ?? sd.Multiplier ?? 0;
    const mult =
        multRaw != null && Number.isFinite(Number(multRaw)) && Number(multRaw) > 0
            ? Number(multRaw)
            : null;
    if (systemKw != null && systemKw > 0 && mult != null) {
        return Math.round(systemKw * ESTIMATED_YIELD_KWH_PER_KW * mult);
    }

    if (!options.snapshotOnly) {
        const viewerReady =
            !siteViewerCalculationsPending &&
            (viewerModeCount ?? 0) > 0 &&
            typeof viewerActualAnnualKwh === 'number' &&
            Number.isFinite(viewerActualAnnualKwh) &&
            viewerActualAnnualKwh > 0;

        if (viewerReady) {
            return Math.round(viewerActualAnnualKwh);
        }
    }

    if (systemKw != null && systemKw > 0) {
        return Math.round(systemKw * ESTIMATED_YIELD_KWH_PER_KW);
    }

    return null;
}
